import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useServices, useLive, useSession, useConversation, useConversationSummary, ProblemError, type Conversation, type RequestOptions } from '@/model';
import { ConsentChanged } from './intent';
import { isPayloadTooLarge, isStaleContentLink } from '@/core/content-links';
import { installJournalCleanup } from './journal';
import { record } from './access';
import type { Navigation, PresentOptions, SheetHandle } from '@/app/contract';
import type { ConversationEnvelope, ProjectOption } from './types';
interface Scope {
    source?: string;
    key: QueryKey;
    current(): boolean;
    sourceCurrent(): boolean;
    own(handle: SheetHandle): () => void;
    refresh(): Promise<void>;
}
const ResourceContext = createContext<Scope | null>(null);
export const nativeReadOptions = {protocol:9,capabilities:['items.v1']} as const;
export const chatBase = (id: string) => `/v1/conversations/${encodeURIComponent(id)}`;
export function Resources({ children, source }: { children: ReactNode; source?: string }) {
    const parent = useContext(ResourceContext);
    return parent && (!source || parent.source === source) ? <>{children}</> : <ResourceRoot key={source} source={source}>{children}</ResourceRoot>;
}
function ResourceRoot({ children, source }: {
    children: ReactNode;
    source?: string;
}) {
    const services = useServices();
    const session = useSession();
    const lifetime = useLive(services.session.lifetime);
    useEffect(() => { installJournalCleanup(services); }, [services]);
    const client = useQueryClient();
    const identity = session.phase === 'signedIn' ? session.accountId : null;
    const [generation, setGeneration] = useState(0);
    const sourceGeneration = useRef(0);
    const mounted = useRef(true);
    const sheets = useRef(new Set<SheetHandle>());
    useEffect(() => { const handles = sheets.current; return () => { for (const handle of [...handles])
        handle.dismiss(); handles.clear(); }; }, []);
    useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
    const context = useQuery({ queryKey: ['hylo', location.origin, null, identity, 'chatinfo', 'context'], enabled: !!identity,
        queryFn: ({ signal }) => services.http.get<{
            realm_id: string;
            mutation_epoch: string;
        }>('/v1/items/context', { signal }), retry: false });
    const key = useMemo(() => ['hylo', location.origin, context.data?.realm_id ?? null, identity, 'chatinfo', generation], [context.data?.realm_id, identity, generation]);
    useEffect(() => services.session.lifetime.subscribe(() => {
        sourceGeneration.current++;
        setGeneration(sourceGeneration.current);
        client.removeQueries({ predicate: q => q.queryKey[4] === 'chatinfo' });
    }), [services, client]);
    useEffect(() => services.sync.onUpdate('*', update => {
        if (/conversation\.moved|member\.(left|role)|project\.(member|removed)|readers\./.test(update.type)) {
            for (const handle of [...sheets.current])
                handle.dismiss();
            sheets.current.clear();
        }
        const prefs = record(update.data);
        // Ordinary notification settings do not revoke an explicitly unrestricted view.
        const keepsVisibility = update.type === 'me.member_prefs' && prefs.cleared_seq === 0 && prefs.hidden_seq === null && prefs.hide_agent_messages === false
            && Object.keys(prefs).every(key => ['conversation_id', 'notify', 'archived', 'mute_until', 'sound', 'cleared_seq', 'hidden_seq', 'review_voice_transcripts', 'hide_agent_messages'].includes(key));
        const privateLoss = !keepsVisibility && /conversation\.moved|message\.(hidden|deleted)|block\.|project\.removed|member\.(left|role)|project\.member\.|me\.member_prefs|readers\.|resync/.test(update.type);
        if (privateLoss) {
            void client.cancelQueries({ queryKey: key });
            client.removeQueries({ queryKey: key });
            sourceGeneration.current++;
            setGeneration(sourceGeneration.current);
        }
        void client.invalidateQueries({ queryKey: key });
    }), [services, client, key]);
    useEffect(() => services.sync.connection.subscribe(() => {
        if (services.sync.connection.get().phase === 'resyncing') {
            void client.cancelQueries({ queryKey: key });
            client.removeQueries({ queryKey: key });
            sourceGeneration.current++;
            setGeneration(sourceGeneration.current);
        }
    }), [services, client, key]);
    const scope = useMemo<Scope>(() => ({ source, key, current: () => mounted.current && lifetime !== null && lifetime === services.session.lifetime.get() && services.session.state.get().phase === 'signedIn', sourceCurrent: () => mounted.current && lifetime !== null && lifetime === services.session.lifetime.get() && services.session.state.get().phase === 'signedIn' && generation === sourceGeneration.current, own: (handle: SheetHandle) => { sheets.current.add(handle); return () => { sheets.current.delete(handle); }; }, refresh: async () => { await client.invalidateQueries({ queryKey: key }); } }), [source, key, lifetime, services, client, generation]);
    return <ResourceContext.Provider value={scope}>{children}</ResourceContext.Provider>;
}
export function useScope() { const scope = useContext(ResourceContext); if (!scope)
    throw new Error('Missing chatinfo resource scope'); return scope; }
export function useResource<T>(suffix: QueryKey, path: string, options: RequestOptions & {
    enabled?: boolean;
    sensitive?: boolean;
} = {}) {
    const services = useServices();
    const scope = useScope();
    const { enabled = true, sensitive = false, ...request } = options;
    return useQuery({ queryKey: [...scope.key, ...suffix], enabled: enabled && scope.current(), gcTime: sensitive ? 0 : 300000,
        queryFn: async ({ signal }) => { const result = await services.http.get<T>(path, { ...request, signal }); if (!scope.sourceCurrent())
            throw new DOMException('Retired source', 'AbortError'); return result; }, retry: false, refetchOnWindowFocus: 'always', staleTime: 0 });
}
export function useInfo(id: string) {
    const services = useServices();
    const core = useConversation(id);
    const summary = useConversationSummary(id);
    useEffect(() => { if (id)
        void services.conversations.ensure(id).catch(() => undefined); }, [id, services]);
    const resource = useResource<ConversationEnvelope>(['detail', id], chatBase(id), { enabled: !!id });
    const raw = resource.data?.conversation;
    const denied = resource.error instanceof ProblemError && [403,404].includes(resource.error.status);
    const conversation: Conversation | undefined = denied ? undefined : core ? { ...raw, ...core } : raw;
    // Retained history keeps its project ID after departure, without granting project access.
    const projectId = conversation && (conversation.me.left_seq == null || conversation.me.access === 'project') ? conversation.project_id : undefined;
    const projectReady = raw && (raw.me.left_seq == null || raw.me.access === 'project') && raw.project_id === projectId;
    const project = useResource<{
        project: ProjectOption;
    }>(['project', projectId], `/v1/projects/${encodeURIComponent(projectId ?? '')}`, { enabled: !!projectId && !!projectReady });
    return { conversation, summary, accounts: resource.data?.accounts ?? [], project: project.data?.project, projectResource: project, resource };
}
export function errorKey(error: unknown, serverRefusals = false): string {
    if (isPayloadTooLarge(error)) return 'spaces:files.tooLargeToPreview';
    if (isStaleContentLink(error) && error instanceof ProblemError && error.code.startsWith('content_link_')) return 'spaces:files.contentLinkChanged';
    if (error instanceof ConsentChanged) return 'contextChanged';
    if (error instanceof ProblemError) {
        const code = error.code;
        if (serverRefusals && error.status === 403) {
            const refusal = ({ forbidden: 'actionForbidden', org_restricted: 'orgRestricted', project_admission: 'projectAdmission',
                project_archived: 'projectArchived', not_member: 'notMember' } as Record<string, string>)[code];
            if (refusal) return refusal;
        }
        if (/revision|epoch|idempotency/.test(code))
            return 'epochChanged';
        if (/forbidden|not_member|viewer|archived/.test(code))
            return 'managementLost';
        if (/group_full|member_cap/.test(code))
            return 'groupFull';
        if (code === 'member_ineligible') return 'memberIneligible';
        if (/agent_bound/.test(code))
            return 'fixedMembership';
    }
    return 'actionFailed';
}
export function validArray<T>(data: unknown, field: string): T[] { const value = record(data)[field]; return Array.isArray(value) ? value as T[] : []; }
/** Modal content is hosted by the shell, so it needs a fresh feature resource boundary. */
export function present(scope: Scope, nav: Navigation, content: ReactNode, options?: PresentOptions) {
    let release = () => { };
    const handle = nav.present(<Resources>{content}</Resources>, { ...options, onDismiss: () => { release(); options?.onDismiss?.(); } });
    release = scope.own(handle);
    return { dismiss: () => { release(); handle.dismiss(); } };
}

/** Keep a multi-request feature action bound to the login that began it. */
export function useBoundServices() {
 const services = useServices(); const scope = useScope();
 return useMemo(() => {
  const namespaces = new WeakMap<object, object>();
  return new Proxy(services, { get(target, key, receiver) {
   const value: unknown = Reflect.get(target, key, receiver);
   if (!value || typeof value !== 'object') return value;
   let wrapper = namespaces.get(value);
   if (!wrapper) {
    wrapper = new Proxy(value, { get(namespace, method, methodReceiver) {
     const member: unknown = Reflect.get(namespace, method, methodReceiver);
     if (typeof member !== 'function') return member;
     return (...args: unknown[]) => {
      if (!scope.current()) throw new DOMException('Retired login', 'AbortError');
      const write = key === 'http' ? (['post','put','patch','delete','upload','uploadRaw'].includes(String(method)) || (['request','response'].includes(String(method)) && !['GET','HEAD','OPTIONS'].includes(String(args[0]).toUpperCase()))) : key === 'media' ? method === 'upload' : key === 'conversations' ? !['list','readable','summary','conversation','ensure','refresh'].includes(String(method)) : key === 'people' ? !['account','contacts','requests','blocked','lookup'].includes(String(method)) : key === 'messages' ? ['forward','send','edit','unsend'].includes(String(method)) : false;
      if (write && !scope.sourceCurrent()) throw new ConsentChanged();
      return Reflect.apply(member, namespace, args);
     };
    } });
    namespaces.set(value, wrapper);
   }
   return wrapper;
  } });
 }, [services, scope]);
}
